import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Select from "react-select";
import { Row, Col, Button, Spinner, Input } from "reactstrap";
import { Drawer, message } from "antd";
import { buyerApis } from "../../../api/buyer";
import { projectsApis } from "../../../api/projects";
import Swal from "sweetalert2";

interface User {
  id: number;
  userName: string;
}

interface Role {
  roleId: number;
  roleName: string;
  users: User[];
}

interface ApprovalLevel {
  roleId: number | null;
  userId: number | null;
  level: number;
  roleName?: string;
  userName?: string;
  approvalStage?: string;
  stateApprovalHierarchyId?: number;
}

interface IssueApprovalDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ApprovalLevel[]) => void;
  setApprovalSubmissionStatus: (status: boolean) => void;
  approvalData: ApprovalLevel[];
  projectId: number;
  // When provided, this user is hidden from the approver dropdown
  // (e.g. task creation excludes the current user as an approver).
  excludeUserId?: number;
}

const IssueApprovalDrawer: React.FC<IssueApprovalDrawerProps> = ({
  open,
  onClose,
  onSubmit,
  setApprovalSubmissionStatus,
  approvalData,
  projectId,
  excludeUserId,
}) => {
  const { register, handleSubmit, setValue, watch, reset } = useForm();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [approvalLevels, setApprovalLevels] = useState<number>(1);
  const [approvalRows, setApprovalRows] = useState<ApprovalLevel[]>([]);

  // Fetch roles with users when component mounts or projectId changes
  const fetchRolesWithUsers = async (
    projectId: number,
    setRoles: Function,
    setLoading: Function
  ) => {
    try {
      setLoading(true);
      const response = await projectsApis.fetchProjectRolesWithUsers(projectId);
      if (response && response.status) {
        setRoles(response.data);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      message.error("Failed to fetch roles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && projectId) {
      fetchRolesWithUsers(projectId, setRoles, setLoading);
    }
  }, [open, projectId]);

  // Initialize form with approvalData if it exists
  useEffect(() => {
    if (open) {
      if (approvalData && approvalData.length > 0) {
        setApprovalLevels(approvalData.length);
        setApprovalRows(
          approvalData.map((item) => ({
            ...item,
            approvalStage: item.approvalStage || "Approver",
          }))
        );
      } else {
        setApprovalLevels(1);
        setApprovalRows([
          {
            roleId: null,
            userId: null,
            level: 1,
            approvalStage: "Approver", // Default first level to Approver
          },
        ]);
      }
    }
  }, [open, approvalData]);

  const handleLevelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Handle empty input
    // Allow empty input while typing
    if (inputValue === "") {
      setApprovalLevels(0); // Temporary state while typing
      return;
    }

    // Parse the input value
    const newLevels = parseInt(inputValue, 10);

    // Validate the input (1-10)
    if (isNaN(newLevels)) {
      return; // Don't update state for invalid values
    }

    setApprovalLevels(newLevels);

    // Update approvalRows array

    if (newLevels >= 1) {
      if (newLevels > approvalRows.length) {
        // Add new rows
        const newRows = [...approvalRows];
        for (let i = approvalRows.length; i < newLevels; i++) {
          newRows.push({
            roleId: null,
            userId: null,
            level: i + 1,
            approvalStage: i === 0 ? "Approver" : undefined,
          });
        }
        setApprovalRows(newRows);
      } else if (newLevels < approvalRows.length) {
        // Remove extra rows
        setApprovalRows((prev) => prev.slice(0, newLevels));
      }
    }
  };

  // Helper: a role's users minus the excluded one (e.g. assignee / current
  // user — they can't approve their own work).
  const getSelectableUsers = (users: User[] = []) =>
    users.filter(
      (user) =>
        excludeUserId == null || Number(user.id) !== Number(excludeUserId)
    );

  // Roles that still have at least one selectable approver after exclusion.
  // If the excluded user is a role's only member, that role has nobody left
  // to pick, so it's hidden from the Role dropdown entirely.
  const selectableRoles = roles.filter(
    (role) => getSelectableUsers(role.users).length > 0
  );

  const handleRoleChange = (roleId: number | null, level: number) => {
    setApprovalRows((prev) =>
      prev.map((row) =>
        row.level === level
          ? {
              ...row,
              roleId,
              userId: null,
              stateApprovalHierarchyId: row.stateApprovalHierarchyId,
            }
          : row
      )
    );
  };

  const isUserAlreadySelected = (
    userId: number | null,
    currentLevel: number
  ): boolean => {
    if (!userId) return false;
    return approvalRows.some(
      (row) => row.userId === userId && row.level !== currentLevel
    );
  };

  const handleUserChange = (userId: number | null, level: number) => {
    if (userId && isUserAlreadySelected(userId, level)) {
      message.error("This user is already selected in another approval level");
      return;
    }
    setApprovalRows((prev) =>
      prev.map((row) => (row.level === level ? { ...row, userId } : row))
    );
  };

  const handleApprovalStageChange = (stage: string | null, level: number) => {
    setApprovalRows((prev) =>
      prev.map((row) =>
        row.level === level
          ? { ...row, approvalStage: stage || undefined }
          : row
      )
    );
  };

  const handleDeleteRow = (level: number) => {
    if (approvalRows.length === 1) {
      message.error("At least one approval level is required");
      return;
    }
    setApprovalRows((prev) => {
      const filtered = prev.filter((row) => row.level !== level);
      // Re-number levels after deletion
      return filtered.map((row, index) => ({
        ...row,
        level: index + 1,
      }));
    });
    setApprovalLevels(approvalRows.length - 1);
  };

  const getUserFullName = (user?: User): string => {
    if (!user) return "";
    return user.userName || "";
  };

  const onSubmitHandler = () => {
    // First check if there's at least 1 approver
    if (approvalRows.length === 0) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please add at least 1 approver",
        confirmButtonText: "OK",
      });
      return;
    }

    // Check if at least one level is set as Approver
    const hasApprover = approvalRows.some(
      (row) => row.approvalStage === "Approver"
    );

    if (!hasApprover) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "At least one approval level must be set as 'Approver'",
        confirmButtonText: "OK",
      });
      return;
    }

    // Then validate all fields
    const isValid = approvalRows.every(
      (row) => row.roleId && row.userId && row.approvalStage
    );
    if (!isValid) {
      message.error("Please complete all fields for all approval levels");
      return;
    }

    // Prepare data for submission
    const submissionData = approvalRows.map((row) => {
      const role = roles.find((r) => r.roleId === row.roleId);
      const user = role?.users?.find((u) => u.id === row.userId);

      return {
        roleId: row.roleId,
        userId: row.userId,
        level: row.level,
        approvalStage: row.approvalStage,
        roleName: role?.roleName || "",
        userName: user?.userName || "",
        ...(row.stateApprovalHierarchyId && {
          stateApprovalHierarchyId: row.stateApprovalHierarchyId,
        }),
      };
    });

    onSubmit(submissionData);
    setApprovalSubmissionStatus(true);
    onClose();
  };

  return (
    <Drawer
      title="Set Work Approvals"
      placement="right"
      onClose={onClose}
      open={open}
      width="50%"
      zIndex={2000}
      destroyOnClose={false}
      maskClosable={false}
      getContainer={() => document.body}
    >
      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "100%" }}
        >
          <Spinner color="primary" />
        </div>
      ) : (
        <div>
          <div className="mb-4">
            <label className="form-label">Number of Approval Levels</label>
            <Input
              // type="text"
              // inputMode="numeric"
              // pattern="[1-9]|10"
              type="number"
              min="1"
              // max="10"
              value={approvalLevels || ""}
              onChange={handleLevelChange}
              // disabled={approvalData?.length > 0}
              className="w-25"
              // onKeyPress={(e) => {
              //   if (!/[0-9]/.test(e.key)) {
              //     e.preventDefault();
              //   }
              // }}
            />
            <small className="text-muted ms-2">
              Main approval is level 1. Add more levels if needed.
            </small>
          </div>

          <div className="approval-levels-container">
            <div className="d-none d-md-block mb-2">
              <Row className="g-2 fw-bold small">
                <Col md={4}>Role</Col>
                <Col md={4}>User</Col>
                <Col md={3}>Approval Stage</Col>
                <Col md={1}>Action</Col>
              </Row>
            </div>

            {approvalRows.map((row, index) => {
              const selectedRole = roles.find((r) => r.roleId === row.roleId);
              // excludeUserId (when passed) is hidden from the approver list
              // — e.g. task creation excludes the current user.
              const roleUsers = getSelectableUsers(selectedRole?.users);

              return (
                <div key={index} className="mb-3 p-2 border rounded">
                  <Row className="g-2 align-items-center">
                    <Col md={4}>
                      <Select
                        options={selectableRoles.map((role) => ({
                          value: role.roleId,
                          label: role.roleName,
                        }))}
                        value={
                          row.roleId
                            ? {
                                value: row.roleId,
                                label: roles.find(
                                  (r) => r.roleId === row.roleId
                                )?.roleName,
                              }
                            : null
                        }
                        onChange={(selected) =>
                          handleRoleChange(selected?.value || null, row.level)
                        }
                        placeholder="Select Role"
                        isClearable
                        className="react-select-container"
                        classNamePrefix="react-select"
                        menuPlacement="auto"
                      />
                    </Col>

                    <Col md={4}>
                      <Select
                        options={roleUsers.map((user) => ({
                          value: user.id,
                          label: user.userName,
                          isDisabled: isUserAlreadySelected(user.id, row.level),
                        }))}
                        value={
                          row.userId && row.roleId
                            ? {
                                value: row.userId,
                                label:
                                  roleUsers.find((u) => u.id === row.userId)
                                    ?.userName || "",
                              }
                            : null
                        }
                        onChange={(selected) =>
                          handleUserChange(selected?.value || null, row.level)
                        }
                        placeholder="Select User"
                        isDisabled={!row.roleId}
                        isClearable
                        className="react-select-container"
                        classNamePrefix="react-select"
                        menuPlacement="auto"
                      />
                    </Col>

                    <Col md={3}>
                      <Select
                        options={[
                          { value: "Checker", label: "Checker" },
                          { value: "Approver", label: "Approver" },
                          { value: "Verifier", label: "Verifier" },
                        ]}
                        value={
                          row.approvalStage
                            ? {
                                value: row.approvalStage,
                                label: row.approvalStage,
                              }
                            : null
                        }
                        onChange={(selected) =>
                          handleApprovalStageChange(
                            selected?.value || null,
                            row.level
                          )
                        }
                        placeholder="Select Stage"
                        isClearable
                        className="react-select-container"
                        classNamePrefix="react-select"
                        menuPlacement="auto"
                      />
                    </Col>

                    <Col md={1}>
                      <em
                        className="icon ni ni-trash"
                        onClick={() => {
                          if (approvalRows.length > 1) {
                            handleDeleteRow(row.level);
                          }
                        }}
                        style={{
                          cursor:
                            approvalRows.length === 1
                              ? "not-allowed"
                              : "pointer",
                          fontSize: "16px",
                          color:
                            approvalRows.length === 1 ? "#d3d3d3" : "#ff4d4f",
                          padding: "4px",
                        }}
                      ></em>
                    </Col>
                  </Row>
                  <div className="text-muted small mt-1">Level {row.level}</div>
                </div>
              );
            })}
          </div>

          <div className="d-flex justify-content-end mt-3">
            <Button color="secondary" onClick={onClose} className="me-2">
              Cancel
            </Button>
            <Button color="primary" onClick={onSubmitHandler}>
              Save Approvals
            </Button>
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default IssueApprovalDrawer;
